<div class="settings-editor" title="User Settings Editor">
  <!-- Loading state -->
  @if (loadingService.loading$ | async) {
    <div class="loading-container">
      <mat-spinner diameter="40"></mat-spinner>
      <p>Loading settings...</p>
    </div>
  }

  <!-- Settings form -->
  @if (!(loadingService.loading$ | async) && userSettings) {
    <div>
      <form class="settings-editor-form" [formGroup]="settingsForm" title="User Settings Editor">
        <div formGroupName="settings">
          <h3 id="settings-heading">User Settings</h3>
          <mat-divider></mat-divider>
          <!-- Warning message -->
          <div class="settings-warning">
            <p><mat-icon>warning</mat-icon>
            <strong>Warning:</strong> Modifying user settings is potentially dangerous and may break application functionality. Make changes only if you understand their impact.</p>
          </div>
          <!-- Existing settings -->
          @if (settingsKeys.length > 0) {
            <div class="settings-list">
              @for (key of settingsKeys; track key) {
                <div class="setting-item">
                  <div class="setting-header">
                    <div class="setting-name">
                      <strong>{{ key }}</strong>
                      @if (getSettingMetadata(key); as meta) {
                        <span class="setting-type"
                          >
                          ({{ meta.type }})
                        </span>
                      }
                    </div>
                    <div class="setting-actions">
                      <mat-form-field appearance="outline" class="type-selector">
                        <mat-label>Type</mat-label>
                        <mat-select
                          [value]="getSettingMetadata(key)?.type"
                          (selectionChange)="updateSettingType(key, $event.value)"
                          [attr.aria-label]="'Select type for ' + key"
                          title="Select type">
                          @for (type of availableTypes; track type) {
                            <mat-option [value]="type.value">
                              {{ type.label }}
                            </mat-option>
                          }
                        </mat-select>
                      </mat-form-field>
                      <button
                        mat-icon-button
                        color="warn"
                        (click)="onRemoveSetting(key)"
                        [attr.aria-label]="'Remove ' + key + ' setting'"
                        title="Remove this setting">
                        <mat-icon>delete</mat-icon>
                      </button>
                    </div>
                  </div>
                  <!-- Different editors based on type -->
                  @if (getSettingMetadata(key); as meta) {
                    <div class="setting-editor">
                      <!-- String editor -->
                      @if (meta.type === 'string') {
                        <mat-form-field appearance="outline" class="full-width">
                          <mat-label>{{ key }}</mat-label>
                          <input
                            matInput
                            [formControlName]="key"
                            [attr.aria-label]="'Edit ' + key + ' value'"
                            [title]="'Edit ' + key + ' value'"
                            [placeholder]="'Enter ' + key + ' value'">
                          </mat-form-field>
                        }
                        <!-- Number editor -->
                        @if (meta.type === 'number') {
                          <mat-form-field appearance="outline" class="full-width">
                            <mat-label>{{ key }}</mat-label>
                            <input
                              matInput
                              type="number"
                              [formControlName]="key"
                              [attr.aria-label]="'Edit numeric value for ' + key"
                              [title]="'Edit ' + key + ' value'"
                              [placeholder]="'Enter numeric value for ' + key">
                            </mat-form-field>
                          }
                          <!-- Boolean editor -->
                          @if (meta.type === 'boolean') {
                            <mat-slide-toggle
                              [formControlName]="key"
                              [attr.aria-label]="'Toggle ' + key + ' value'"
                              [title]="'Toggle ' + key + ' value'">
                              {{ getSettingControl(key)?.value === 'true' ? 'True' : 'False' }}
                            </mat-slide-toggle>
                          }
                          <!-- Object/Array editor -->
                          @if (meta.isComplex) {
                            <div class="complex-editor">
                              <div class="edit-mode-toggle">
                                <button
                                  mat-button
                                  color="primary"
                                  (click)="toggleJsonEditMode(key)"
                                  [attr.aria-label]="'Toggle edit mode for ' + key"
                                  title="Toggle edit mode">
                                  {{ jsonEditMode[key] ? 'View' : 'Edit' }}
                                </button>
                              </div>
                              <!-- JSON viewer (non-edit mode) -->
                              @if (!jsonEditMode[key]) {
                                <div class="json-viewer">
                                  <pre>{{ formatJson(getSettingControl(key)?.value) }}</pre>
                                </div>
                              }
                              <!-- JSON editor -->
                              @if (jsonEditMode[key]) {
                                <div class="json-editor">
                                  <mat-form-field appearance="outline" class="full-width">
                                    <mat-label>{{ key }} (JSON)</mat-label>
                                    <textarea
                                      matInput
                                      [formControlName]="key"
                                      rows="10"
                                      [attr.aria-label]="'Edit ' + key + ' as JSON'"
                                      [title]="'Edit ' + key + ' as JSON'"
                                    [placeholder]="meta.type === 'object' ? 'Enter JSON object' : 'Enter JSON array'"></textarea>
                                  </mat-form-field>
                                  <div class="json-hint">
                                    <p>Format: {{ meta.type === 'object' ? objectSample : arraySample }}</p>
                                  </div>
                                </div>
                              }
                            </div>
                          }
                          <!-- Default editor -->
                          @if (!meta.isComplex && meta.type !== 'string' && meta.type !== 'number' && meta.type !== 'boolean') {
                            <mat-form-field appearance="outline" class="full-width">
                              <mat-label>{{ key }}</mat-label>
                              <input
                                matInput
                                [formControlName]="key"
                                [attr.aria-label]="'Edit ' + key + ' value'"
                                [title]="'Edit ' + key + ' value'"
                                [placeholder]="'Enter value for ' + key">
                              </mat-form-field>
                            }
                          </div>
                        }
                      </div>
                    }
                  </div>
                }
                <!-- No settings message -->
                @if (settingsKeys.length === 0) {
                  <div class="no-settings">
                    <p>You don't have any settings yet. Add your first setting below.</p>
                  </div>
                }
                <!-- Add new setting form -->
                <div class="add-setting-form">
                  <h4>Add New Setting</h4>
                  <div class="add-setting-inputs">
                    <!-- Key input -->
                    <mat-form-field appearance="outline">
                      <mat-label>Setting Name</mat-label>
                      <input
                        matInput
                        [(ngModel)]="newSettingKey"
                        [ngModelOptions]="{standalone: true}"
                        title="Enter setting name">
                      </mat-form-field>
                      <!-- Type selector -->
                      <mat-form-field appearance="outline">
                        <mat-label>Type</mat-label>
                        <mat-select
                          [(ngModel)]="newSettingType"
                          [ngModelOptions]="{standalone: true}"
                          title="Select setting type">
                          @for (type of availableTypes; track type) {
                            <mat-option [value]="type.value">
                              {{ type.label }}
                            </mat-option>
                          }
                        </mat-select>
                      </mat-form-field>
                      <!-- Value input -->
                      <mat-form-field appearance="outline" class="value-input">
                        <mat-label>Value</mat-label>
                        <input
                          matInput
                          [(ngModel)]="newSettingValue"
                          [ngModelOptions]="{standalone: true}"
                          [placeholder]="getDefaultValueForType(newSettingType)"
                          title="Enter setting value">
                          @if (newSettingType === 'object') {
                            <mat-hint>Format: {{ objectSample }}</mat-hint>
                          }
                          @if (newSettingType === 'array') {
                            <mat-hint>Format: {{ arraySample }}</mat-hint>
                          }
                        </mat-form-field>
                        <!-- Add button -->
                        <button
                          mat-raised-button
                          color="primary"
                          (click)="onAddSetting()"
                          title="Add this setting">
                          Add
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
                <!-- Save button -->
                <div class="save-actions">
                  <button
                    mat-raised-button
                    color="primary"
                    (click)="onSaveSettings()"
                    [disabled]="loadingService.loading$ | async"
                    title="Save all settings">
                    @if (!(loadingService.loading$ | async)) {
                      <mat-icon>save</mat-icon>
                    }
                    @if (loadingService.loading$ | async) {
                      <mat-spinner diameter="20" class="save-spinner"></mat-spinner>
                    }
                    {{ (loadingService.loading$ | async) ? 'Saving...' : 'Save Settings' }}
                  </button>
                  <!-- Save status -->
                  @if (saveSuccess) {
                    <div class="save-status">
                      <mat-icon color="primary">check_circle</mat-icon>
                      <span class="success-text">Settings saved successfully</span>
                    </div>
                  }
                  @if (saveError) {
                    <div class="save-status">
                      <mat-icon color="warn">error</mat-icon>
                      <span class="error-text">Error saving settings</span>
                    </div>
                  }
                </div>
              </div>
            }
          </div>
